import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injectable,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import {
    Setting,
    UserAuthState,
} from '@app-state';
import {
    IPackage,
    NugetType,
    ODataResponse,
} from '@models';
import {
    GRID_SERVICE,
    IGridSource,
    ODataGridComponent,
} from '@pages/generics/grid';
import {
    CommonNugetService,
    FileDownloadService,
    LibraryService,
    MapperService,
    ModalService,
    ODataQueryOptions,
    PackageService,
} from '@services';

import {
    forkJoin,
    Observable,
    ReplaySubject,
} from 'rxjs';
import {
    distinctUntilChanged,
    map,
    switchMap,
    takeUntil,
} from 'rxjs/operators';

@Injectable()
export class NugetVersionSource implements IGridSource<ODataQueryOptions, ODataResponse<IPackage>> {
    public service!: LibraryService | PackageService;
    public packageId!: string;

    public get mapper() {
        return MapperService.packageMap;
    }

    public get(qo: ODataQueryOptions) {
        return this.service.getVersions(this.packageId, qo)
            .pipe(
                map(
                    res => {
                        res.data = res.data
                            .map(pkg => ({
                                ...pkg,
                                id: pkg.key,
                            }));
                        return res;
                    },
                ),
            );
    }
}

@Component({
    selector: 'ui-nuget-versions-grid',
    templateUrl: './nuget-versions.grid.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        CommonNugetService,
        {
            provide: GRID_SERVICE,
            useClass: NugetVersionSource,
        },
    ],
    styleUrls: ['./nuget-versions.grid.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class NugetVersionsGridComponent
    extends ODataGridComponent<IPackage, string>
    implements OnInit, OnChanges {

    public get allowDelete(): boolean {
        return this._grid.selectionManager.selected.every(process => !process.isActive);
    }

    public get isRemoveAllEnabled(): boolean {
        return this._grid.dataManager.some(process => !process.isActive);
    }

    public get showDeleteButton() {
        if (
            this._appState.user.authState === UserAuthState.LoggedInAsTenant &&
            this.type === NugetType.Libraries &&
            this._appState.settings.get(Setting.librariesUseSharedFeed)
        ) {
            return false;
        }

        return true;
    }

    public get isDownloadEnabled(): boolean {
        return this._commonNugetService.isNugetDownloadEnabled(this.type);
    }

    public NugetType = NugetType;

    @Input()
    public packageId!: string;

    @Input()
    public nugetService!: LibraryService | PackageService;

    @Input()
    public type!: NugetType;

    @Input()
    public deletePermission!: string;

    @Output()
    public close = new EventEmitter<boolean>();

    @Output()
    public showInformation = new EventEmitter<IPackage>();

    @Output()
    public showReleaseNotes = new EventEmitter<IPackage>();

    @Output()
    public latestVersions = new EventEmitter<IPackage[]>();

    public isDeletePermissionGranted$!: Observable<boolean>;

    private _modalService = this._injector.get<ModalService>(ModalService);
    private _downloadService = this._injector.get<FileDownloadService>(FileDownloadService);
    private _commonNugetService = this._injector.get<CommonNugetService>(CommonNugetService);
    private _deletePermission$ = new ReplaySubject<string>(1);

    ngOnInit() {
        const svc = (this._service as NugetVersionSource);
        svc.packageId = this.packageId;
        svc.service = this.nugetService;

        this.data$.pipe(
            takeUntil(this._destroyed$),
        ).subscribe(data => {
            this.latestVersions.emit(data);
            this.latestVersions.complete();
        });

        this.isDeletePermissionGranted$ = this._deletePermission$
            .pipe(
                distinctUntilChanged(),
                switchMap(() => this._appState.user.permissions.isGranted(this.deletePermission)),
            );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.deletePermission) {
            this._deletePermission$.next(changes.deletePermission.currentValue);
        }
    }

    public downloadLink(entry: IPackage) {
        return this.nugetService.downloadLink(entry);
    }

    public downloadPackage(event: MouseEvent, entry: IPackage) {
        event.preventDefault();
        this._downloadService.download(this.nugetService.downloadLink(entry));
    }

    public onDeleteInactive() {
        const promptMessage = this._translate.instant('package_versions_modal_remove_inactive_message');
        this._modalService.onConfirm(null, () => {
            this.nugetService.delete(this.packageId).subscribe(() => {
                this.close.emit(true);
            });
        }, () => { }, promptMessage);
    }

    public onDeleteVersion() {
        const promptMessage =
            this.type === NugetType.Libraries ?
                this._translate.instant('package_versions_modal_remove_selected_libraries_message') :
                this._translate.instant('package_versions_modal_remove_selected_message');

        this._modalService.onConfirm(null, () => {
            const selectedKeys = this._grid.selectionManager.selected.map((r) => r.key);
            const deleteSelectedKeysObservables = selectedKeys.map(key => this.nugetService.delete(key));

            forkJoin(deleteSelectedKeysObservables)
                .subscribe(() => {
                    this.close.emit(true);
                });

        }, () => { }, promptMessage);
    }

    public onClose() {
        if (this._grid.dataManager.length === 0) {
            this.close.emit(true);
        } else {
            this.close.emit();
        }
    }
}
